import { FormInstance } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  CheckboxSelect,
  Form,
  Icon,
  ICONS,
  Loader,
  PLATFORM_THEME_COLORS,
  SelectInput,
  Typography,
  TYPOGRAPHY_SIZE,
} from '@thinknet-gmbh/platform-common-ui';

import {
  MainContainer,
  FormListContent,
  DeleteContainer,
} from 'src/pages/jobs/components/job-form/job-form.styles';
import { useGetDocumentLabelsListByCompany } from 'src/hooks/documents-labels/get-documents-labels.hook';
import { useGetCurrentCompanyId } from 'src/hooks/companies/get-current-company-id.hook';

const DocumentFormList = ({
  isEdit,
  documents,
  form,
}: {
  isEdit?: boolean;
  documents: any;
  form: FormInstance<any>;
}) => {
  const { t } = useTranslation(['common', 'jobs']);

  const [selectedLabelsIds, setSelectedLabelsIds] = useState<any[]>(
    documents?.map((doc: any) => doc.documentLabelId) || []
  );

  const currentCompanyId = useGetCurrentCompanyId();
  const { documentsLabels, loading } = useGetDocumentLabelsListByCompany(currentCompanyId);

  if (loading) {
    return <Loader />;
  }

  return (
    <MainContainer>
      <Typography weight="bold" size={TYPOGRAPHY_SIZE.medium}>
        {t('jobs:labels.document')}
      </Typography>
      <Form.List name="documents" initialValue={isEdit ? undefined : [{}]}>
        {(fields, { add, remove }) => (
          <FormListContent isIcon={fields.length > 1}>
            {fields.map(field => (
              <div key={field.key}>
                <DeleteContainer isIcon={fields.length > 1}>
                  <Form.Item
                    name={[field.name, 'documentLabelId']}
                    label={t('common:labels.label')}
                    rules={[
                      { required: true, message: t('common:validation.error.labelRequired') },
                    ]}
                    getValueFromEvent={(value: string) => {
                      setSelectedLabelsIds({ ...selectedLabelsIds, [field.key]: value });
                      return value;
                    }}
                  >
                    <SelectInput
                      placeholder={t('jobs:labels.contract')}
                      onSelect={() => {
                        const doc = form.getFieldValue('documents');
                        Object.assign(doc[field.key], { documentTemplatesIds: undefined });
                        form.setFieldsValue({ doc });
                      }}
                    >
                      {documentsLabels.map((label: any) => (
                        <SelectInput.Option
                          key={label._id}
                          value={label._id}
                          disabled={Object.values(selectedLabelsIds).includes(label._id)}
                        >
                          {label.name}
                        </SelectInput.Option>
                      ))}
                    </SelectInput>
                  </Form.Item>
                  {fields.length > 1 && (
                    <Icon
                      icon={ICONS.trash}
                      size={22}
                      color={PLATFORM_THEME_COLORS.tone.red}
                      rightMargin={false}
                      onClick={() => remove(field.name)}
                    />
                  )}
                </DeleteContainer>
                <Form.Item noStyle shouldUpdate>
                  {({ getFieldValue }) =>
                    getFieldValue(['documents', field.name, 'documentLabelId']) && (
                      <Form.Item
                        name={[field.name, 'documentTemplatesIds']}
                        label={t('jobs:labels.document')}
                        rules={[{ required: true, message: t('jobs:validation.documentRequired') }]}
                      >
                        <CheckboxSelect
                          placeholder={t('jobs:labels.contract')}
                          optionData={documentsLabels
                            .find((label: any) => label._id === selectedLabelsIds[field.key])
                            ?.documentTemplates.map((template: any) => ({
                              value: template._id,
                              label: template.title,
                            }))}
                        />
                      </Form.Item>
                    )
                  }
                </Form.Item>
              </div>
            ))}
            <Button
              type="text"
              icon={ICONS.plus}
              iconColor={PLATFORM_THEME_COLORS.primary.blue}
              onClick={() => add()}
            >
              {t('jobs:buttons.addDocument')}
            </Button>
          </FormListContent>
        )}
      </Form.List>
    </MainContainer>
  );
};

export default DocumentFormList;
